// Mirrors of sculpt-api's Pydantic schemas (sculpt/users/schemas.py, sculpt/orgs/schemas.py,
// sculpt/accounts/schemas.py, sculpt/usage/schemas.py, sculpt/billing/schemas.py). Decimals arrive as
// strings ("20.00"); dates as ISO strings.

export type Locale = 'en' | 'fr';
export type Role = 'owner' | 'admin' | 'member';
export type OrgKind = 'personal' | 'team';
export type OrgStatus = 'active' | 'suspended' | 'archived';

export interface OrganizationBrief {
  id: number;
  name: string;
  kind: OrgKind;
  status: OrgStatus;
  role: Role;
  /** The key of the current subscription's offer ("" without one). */
  plan_name: string;
  /** Since when its owner keeps it out of the review's sample; null: in it. */
  review_sample_opt_out_at?: string | null;
}

export interface Identity {
  provider: string;
  email: string | null;
  tenant_id: string | null;
  display_name: string | null;
  last_used_at: string | null;
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  locale: Locale;
  email_verified: boolean;
  sso_only: boolean;
  last_connection: string | null;
  created_at: string | null;
  organization: OrganizationBrief | null;
  identities: Identity[];
  /** Since when the person objects to the review's sample reading their conversations (staff record it on request). */
  review_sample_objection_at: string | null;
  /** False until the question of the first connection is answered or skipped (pages/DiscoveryPage.tsx). */
  discovery_asked: boolean;
}

/** The answers to the question of the first connection (sculpt-api `users/schemas.py`), in the order shown. */
export const DISCOVERY_SOURCES = ['linkedin', 'google', 'youtube', 'word_of_mouth', 'other'] as const;
export type DiscoverySource = (typeof DISCOVERY_SOURCES)[number];

export interface Tokens {
  access_token: string;
  token_type: string;
  refresh_token: string | null;
}

export interface SignupRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  locale: Locale;
  invitation_token?: string;
}

export interface SignupResponse extends Tokens {
  refresh_token: string;
  user: User;
}

export interface InvitationPreview {
  organization: string;
  email: string;
  role: Role;
  inviter: string | null;
}

export interface Organization {
  id: number;
  name: string;
  kind: OrgKind;
  status: OrgStatus;
  billing_email: string | null;
  billing_name: string | null;
  billing_address: string | null;
  vat_number: string | null;
  /** The billing profile (sculpt-api PLAN-payments.md §3): who is billed, where, and what VIES said of the VAT
   *  number. Sculpt is sold to professionals only: `individual` is a sole trader. */
  customer_kind: CustomerKind | null;
  billing_street: string | null;
  billing_zip: string | null;
  billing_city: string | null;
  /** ISO 3166-1 alpha-2. */
  billing_country: string | null;
  siren: string | null;
  registration_number: string | null;
  vat_status: VatStatus;
  vat_checked_at: string | null;
  /** Everything an invoice needs is there (kind, name, address, country, and the SIREN of a French customer). */
  billing_complete: boolean;
  created_at: string;
  member_count: number;
  role: Role;
  subscription: SubscriptionBrief | null;
  /** What the organization is priced and billed in (ISO 4217), and whether that is settled: its billing country is
   *  set, or it paid for something. Until then the pricing shows the currency guessed or chosen (PLAN-multi-currency.md
   *  C2), and an order in it moves the organization to it. */
  currency: string;
  currency_fixed: boolean;
  /** The limit per period every member without their own inherits, in `currency` (the `_eur` names are historical);
   *  null = no default. */
  default_member_cap_eur: string | null;
  /** A whole period's monthly credits ÷ seats, rounded down to the unit; null without a subscription or credits. */
  suggested_member_cap_eur: string | null;
  /** Every model this deployment offers, each with whether the organization allows it (sculpt-api PLAN-model-choice.md). */
  models: ModelInfo[];
  /** Since when the organization is out of the review's sample (null: in it), and whether the caller may change it:
   *  the owner, on a paid offer (sculpt-api `PUT /organization/review-sample`). */
  review_sample_opt_out_at: string | null;
  review_sample_opt_out_available: boolean;
}

/** One model of the deployment's catalogue (sculpt-api `ModelOut`). Country and region codes are ISO 3166-1 alpha-2,
 *  plus `EU`. */
export interface ModelInfo {
  id: string;
  label: string;
  maker: string;
  host: string;
  host_country: string;
  data_location: string[];
  /** 1 (cheap) or 2 (dear), from the list price (sculpt-api `catalog.PRICE_TIERS`). Absent from an older API. */
  price_tier?: number;
  /** False when the organization refused it: it is never called for the organization. */
  allowed: boolean;
}

export interface OrganizationPatch {
  name?: string;
  billing_email?: string | null;
  billing_name?: string | null;
  billing_address?: string | null;
  vat_number?: string | null;
  customer_kind?: CustomerKind | null;
  billing_street?: string | null;
  billing_zip?: string | null;
  billing_city?: string | null;
  billing_country?: string | null;
  siren?: string | null;
  registration_number?: string | null;
}

export type CustomerKind = 'company' | 'individual';

/** VIES's answer on a number as it is typed (`POST /organization/vat-check`, PLAN-payments.md P13). `blocking`: the
 *  profile cannot be saved with it; `reason` is then the refusal the save would get. */
export interface VatCheck {
  vat_number: string;
  status: 'valid' | 'invalid' | 'unavailable' | 'not_checked';
  name: string | null;
  address: string | null;
  blocking: boolean;
  reason: 'invalid_vat_number' | 'vat_country_mismatch' | 'vat_number_invalid' | 'vies_unavailable' | null;
}
/** `review`: VIES could not answer; staff or a later VIES answer settle it (PLAN-payments.md P9). */
export type VatStatus = 'none' | 'valid' | 'invalid' | 'review';
/** `review`: the order is held (no VAT yet, not payable) until the VAT number is settled. */
export type VatScheme = 'standard' | 'reverse_charge' | 'outside_scope' | 'review';

/** The VAT an order or a quote carries; all null while the billing profile does not say kind and country. */
export interface VatFields {
  vat_scheme: VatScheme | null;
  /** "0.2000"; null when held for review. */
  vat_rate: string | null;
  vat_amount: string | null;
  /** Including VAT. */
  total: string | null;
}

export interface Member {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: Role;
  email_verified: boolean;
  is_active: boolean;
  joined_at: string;
  last_connection: string | null;
  /** The member's own limit on their spend per period, in the organization's currency (the `_eur` names are
   *  historical); null when they inherit the default or are exempt. */
  cap_eur: string | null;
  /** Where the limit that applies comes from: their own, the organization's default, or none. */
  cap_source: CapSource;
  /** The limit that applies, in the organization's currency; null = no limit. */
  effective_cap_eur: string | null;
}

export type CapSource = 'member' | 'default' | 'none';

/** `PUT /organization/members/{id}/cap`: inherit the default, an own limit, or (`cap_eur` null) no limit. */
export type MemberCapChoice = { use_default: true } | { cap_eur: string } | { cap_eur: null };

export interface Invitation {
  id: number;
  email: string;
  role: Role;
  created_at: string;
  expires_at: string;
  invited_by: number | null;
}

export type ClaimKind = 'domain' | 'tenant';

export interface Claim {
  id: number;
  kind: ClaimKind;
  value: string;
  auto_join: boolean;
  verified_at: string | null;
  created_at: string;
}

/** The subscription's pool for the period (sculpt/usage/schemas.py). The `_usd` names are historical: amounts
 *  are in `currency`, the organization's. In scope `member` (a plain member viewing) the amounts are the viewer's own: `spent_usd`
 *  their spend, `budget_usd` their limit (0 without one), `credits` 0, `budget_full` and `prorated_since` null. */
export interface UsageSummary {
  /** `member` when the viewer's role is member; absent on an older API (read as `organization`). */
  scope?: UsageScope;
  spent_usd: number;
  budget_usd: number;
  remaining_usd: number;
  request_count: number;
  limit_reached: boolean;
  unlimited: boolean;
  period: 'trial' | 'month';
  period_start: string | null;
  period_end: string | null;
  organization_id: number | null;
  currency: string;
  reason: LimitReason | null;
  offer: string | null;
  /** The subscription costs nothing a month (Free): the plan strip offers "Upgrade". */
  free: boolean;
  subscription_status: SubscriptionStatus | null;
  /** A whole period's pool; differs from `budget_usd` when a change paid mid-period prorated it. */
  budget_full: number | null;
  /** When a change paid mid-period prorated this period's pool (PLAN-offers.md §7.2); null = a full period. */
  prorated_since: string | null;
  credits: number;
  user_spent: number;
  user_cap: number | null;
  /** The limit that applies to this member: their own, else the organization's default; null = none. */
  member_cap: number | null;
}

export type UsageScope = 'organization' | 'member';

export type LimitReason = 'no_subscription' | 'free_full' | 'trial_ended' | 'payment_overdue' | 'member_cap_reached' | 'user_cap_reached' | 'limit_reached';

export interface MemberUsage {
  user_id: number;
  email: string;
  name: string;
  spent_usd: number;
  request_count: number;
  /** The limit that applies to this member (their own, else the default); null = none. */
  cap: number | null;
}

export interface OrganizationUsage extends UsageSummary {
  /** In scope `member`, only the viewer's own line. */
  members: MemberUsage[];
}

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'void';

export type InvoiceKind = 'invoice' | 'credit_note';

/** An invoice or a credit note. From PLAN-payments.md M2 they are issued through Qonto, which numbers them; the
 *  ones staff recorded before have no VAT fields (`total` null: `amount` is the whole). */
export interface Invoice {
  id: number;
  organization_id: number | null;
  number: string | null;
  kind: InvoiceKind;
  credits_invoice_id: number | null; // a credit note: the invoice it cancels
  order_id: number | null;
  status: InvoiceStatus;
  currency: string;
  amount: string; // excluding VAT
  vat_scheme: VatScheme | null;
  vat_rate: string | null;
  vat_amount: string | null;
  total: string | null; // including VAT
  description: string;
  lines: { description: string; quantity: string; unit_price: string }[];
  period_start: string | null;
  period_end: string | null;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
  sent_at: string | null;
  pdf_name: string | null;
  has_pdf: boolean;
  external_ref: string | null;
  created_at: string;
  updated_at: string;
}

// ── billing (sculpt/billing/schemas.py, PLAN-offers.md §8) ──

export interface OfferTerms {
  id: number;
  version: number;
  currency: string;
  /** Per user per month, excluding VAT. */
  price_per_seat: string;
  period: string;
  /** Usage each user adds to the organization's pool per month (the trial: the whole trial's), in `currency` like
   *  every amount of the terms (the `_eur` names are historical); null = unlimited. */
  allowance_per_seat_eur: string | null;
  /** What one member may draw from the pool per month; null = no cap. */
  user_cap_eur: string | null;
  min_seats: number;
  max_seats: number | null;
  trial_days: number | null;
  features: string[];
  /** The monthly usage tiers to choose from (Solo, Team), cheapest first; null = no choice (PLAN-offers.md §15). */
  credit_tiers: CreditTier[] | null;
}

/** A monthly usage level: `price` excluding VAT buys `usage`, both in the offer's currency. */
export interface CreditTier {
  price: string;
  usage: string;
}

/** An offer as sold in one currency: the same `key` (`free`, `solo`, `team`) in every currency on sale. */
export interface Offer {
  key: string;
  name: string;
  description: string;
  trial: boolean;
  terms: OfferTerms;
}

export interface CreditPack {
  key: string;
  name: string;
  currency: string;
  price: string;
  /** The usage it gives, in `currency`. */
  credit_eur: string;
  validity_months: number | null;
}

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'ended' | 'canceled';

export interface SubscriptionBrief {
  offer_key: string;
  offer_name: string;
  version: number;
  status: SubscriptionStatus;
  seats: number;
  trial: boolean;
  price_per_seat: string;
  credit_tier_price: string | null;
  credit_tier_usage: string | null;
  /** Tier price + users × price per user, excluding VAT. */
  monthly_total: string;
  currency: string;
  current_period_end: string;
}

export interface ScheduledChange {
  offer_key: string;
  offer_name: string;
  version: number;
  seats: number;
  price_per_seat: string;
  credit_tier_price: string | null;
  effective_at: string;
}

export type OrderKind = 'subscribe' | 'change' | 'seats' | 'renewal' | 'pack';
export type OrderStatus = 'pending' | 'paid' | 'canceled' | 'failed';

/** Mollie's statuses of a card payment. */
export type PaymentStatus = 'open' | 'pending' | 'authorized' | 'paid' | 'failed' | 'canceled' | 'expired';

/** POST /organization/orders/{id}/checkout: where to send the admin to pay (Mollie's page). */
export interface CheckoutOut {
  order_id: number;
  payment_id: number;
  checkout_url: string | null;
  status: PaymentStatus;
}

/** GET|POST /organization/card: the saved card, and the latest card update (sculpt-api PLAN-payments.md M4). */
export interface CardOut {
  card_label: string | null;
  card_expires_on: string | null;
  update_status: PaymentStatus | null; // the latest update's Mollie status; null: none
  checkout_url: string | null;         // set while an update is under way on Mollie's page
}

export interface Order extends VatFields {
  id: number;
  organization_id: number;
  kind: OrderKind;
  status: OrderStatus;
  currency: string;
  /** Excluding VAT. */
  amount: string;
  /** When a held order became payable (its VAT number settled). */
  payable_at: string | null;
  /** The latest card payment's Mollie status (PLAN-payments.md M3); null: none started. */
  payment_status: PaymentStatus | null;
  lines: { description: string; quantity: string | number; unit_price: string }[];
  subscription_id: number | null;
  invoice_id: number | null;
  provider: string;
  created_at: string;
  paid_at: string | null;
  canceled_at: string | null;
}

export interface Subscription extends SubscriptionBrief {
  id: number;
  offer_version_id: number;
  started_at: string;
  current_period_start: string;
  cancel_at_period_end: boolean;
  allowance_per_seat_eur: string | null;
  user_cap_eur: string | null;
  min_seats: number;
  max_seats: number | null;
  /** The pool, `pool_full`, `pool_prorated_since`, `pool_spent` and `credits` are null for a plain member; in
   *  `currency`, the organization's. */
  pool: string | null;
  /** A whole period's pool on these terms; differs from `pool` when prorated. */
  pool_full: string | null;
  /** When a change paid mid-period prorated this period's pool; null = a full period. */
  pool_prorated_since: string | null;
  pool_spent: string | null;
  credits: string | null;
  seats_used: number;
  scheduled: ScheduledChange | null;
  pending_order: Order | null;
  newer_version: boolean;
}

export interface BillingState {
  subscription: Subscription | null;
  /** `packs_only`: no subscription, but pack credits keep the assistant on until they run out. */
  reason: 'no_subscription' | 'packs_only' | 'free_full' | 'trial_ended' | 'payment_overdue' | null;
  /** How to pay by transfer: empty for an organization paying by card once card payments are on (P4). */
  payment_instructions: string;
  /** The portal offers « Payer par carte » (PLAN-payments.md M3). */
  payments_enabled: boolean;
  /** The card saved at the last payment, "Visa •••• 4242", and its expiry date. */
  card_label: string | null;
  card_expires_on: string | null;
}

export interface Quote extends VatFields {
  offer_key: string;
  offer_name: string;
  version: number;
  seats: number;
  credit_tier_price: string | null;
  credit_tier_usage: string | null;
  monthly_total: string;
  /** "on_payment": an order to pay, applied once paid; "period_end": scheduled, nothing to pay now. */
  applies: 'on_payment' | 'period_end';
  order_kind: OrderKind | null;
  amount: string;
  currency: string;
  lines: Order['lines'];
  period_end: string | null;
}

/** POST /organization/credits/preview: a pack's price for this organization (VAT null while the profile is
 *  incomplete). */
export interface PackQuote extends VatFields {
  pack_key: string;
  name: string;
  credit_eur: string;
  validity_months: number | null;
  /** Excluding VAT. */
  amount: string;
  currency: string;
}

/** `POST /organization/subscription[/preview]`. `currency`: the one the prices were shown in; omitted, the
 *  organization's. Another one moves the organization to it while its currency is not fixed (else `currency_mismatch`). */
export interface ChangeRequest {
  offer_key: string;
  seats: number;
  credit_tier: string | null;
  currency?: string;
}

/** `GET /pricing/context` (public): the currency the pricing opens in, guessed from the visitor's address, else their
 *  browser's language, else EUR — never what is charged — and the currencies on sale, EUR first. */
export interface PricingContext {
  /** ISO 3166-1 alpha-2; null when nothing told. */
  country: string | null;
  currency: string;
  source: 'ip' | 'language' | 'default';
  currencies: string[];
}

export interface ChangeResult {
  quote: Quote;
  order: Order | null;
  /** A free change, applied at once. */
  applied: boolean;
}

/** A credit grant, its amounts in `currency`, the organization's (the `_eur` names are historical). */
export interface CreditGrant {
  id: number;
  source: 'pack' | 'staff';
  currency: string;
  amount_eur: string;
  remaining_eur: string;
  expires_at: string | null;
  note: string;
  created_at: string;
}

/** Error codes the API answers in `detail` (sculpt/accounts/routes.py, sculpt/orgs/routes.py). */
export type ApiErrorCode =
  | 'email_taken'
  | 'email_plus_address'
  | 'email_disposable_domain'
  | 'weak_password'
  | 'bad_token'
  | 'bad_invitation'
  | 'invitation_mismatch'
  | 'invitation_not_found'
  | 'rate_limited'
  | 'wrong_password'
  | 'owner_must_transfer'
  | 'claim_taken'
  | 'claim_refused'
  | 'already_member'
  | 'not_allowed'
  | 'admin_only'
  | 'owner_only'
  | 'use_leave'
  | 'no_organization'
  | 'organization_unavailable'
  | 'seats_full'
  | 'seats_full_scheduled'
  | 'seats_below_members'
  | 'seats_out_of_range'
  | 'no_change'
  | 'trial_not_selectable'
  | 'offer_not_found'
  | 'pack_not_found'
  | 'nothing_to_cancel'
  | 'nothing_scheduled'
  | 'renewal_is_owed'
  | 'wrong_order_status'
  | 'member_not_found'
  | 'invalid_country'
  | 'invalid_customer_kind'
  | 'invalid_siren'
  | 'invalid_vat_number'
  | 'vat_country_mismatch'
  | 'vat_number_required'
  | 'vat_number_invalid'
  | 'vies_unavailable'
  | 'payments_disabled'
  | 'no_model_left'
  | 'owner_only'
  | 'free_offer'
  | 'unknown_model'
  | 'order_not_payable'
  | 'vat_review_pending'
  | 'billing_profile_required'
  | 'nothing_to_pay'
  | 'payment_provider_unavailable'
  | 'payment_in_progress'
  | 'currency_mismatch'
  | 'currency_locked'
  | 'currency_not_offered';

/** Codes the Microsoft flow lands with in the fragment (`#error=…`, sculpt/auth/routes.py). */
export type SsoErrorCode = 'sso_failed' | 'cancelled' | 'account_disabled' | 'staff_only' | 'email_plus_address' | 'email_disposable_domain' | 'link_mismatch';
